import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, Search, ChevronDown, Plus } from 'lucide-react';
import { useOrders } from '../context/OrdersContext';
import { useProducts } from '../context/ProductsContext';
import { usePaymentTypes } from '../context/PaymentTypeContext';
import { useSettings } from '../context/SettingsContext';

/* ─── Field wrapper ─────────────────────────────────────── */
const Field = ({ label, required, helper, counter, maxLen, valueLen, children, error }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-2">
      {required && <span className="w-2.5 h-2.5 rounded-full bg-teal-500 shrink-0" />}
      <label className="text-sm font-semibold text-gray-700">{label}{required && ' *'}</label>
    </div>
    {children}
    <div className="flex justify-between mt-1 px-0.5">
      {error ? <p className="text-xs text-red-500">{error}</p>
        : helper ? <p className="text-xs text-gray-400">{helper}</p>
        : <span />}
      {counter && <p className="text-xs text-gray-400">{valueLen ?? 0}/{maxLen}</p>}
    </div>
  </div>
);

const inp =
  'w-full px-4 py-2.5 rounded-lg border border-[#e0d5c5] bg-gray-50 text-sm ' +
  'text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 ' +
  'focus:ring-teal-500 focus:border-transparent transition';

/* ─── Helpers ───────────────────────────────────────────── */
const truncate = (str, max) => str.length <= max ? str : str.slice(0, max) + '…';

const highlightMatch = (text, query) => {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 text-yellow-800 rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

/* ─── Generic Portal Dropdown ───────────────────────────── */
const PortalDropdown = ({ triggerRef, open, onClose, children, minWidth = 200 }) => {
  const [pos, setPos] = useState(null);
  const portalRef = useRef(null);

  const reposition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const maxH = 300;
    if (spaceBelow >= 180 || spaceBelow >= spaceAbove) {
      setPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, minWidth), maxHeight: Math.min(maxH, spaceBelow - 8) });
    } else {
      setPos({ top: rect.top - Math.min(maxH, spaceAbove - 8) - 4, left: rect.left, width: Math.max(rect.width, minWidth), maxHeight: Math.min(maxH, spaceAbove - 8) });
    }
  };

  useEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!triggerRef.current?.contains(e.target) && !portalRef.current?.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={portalRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, zIndex: 99999, display: 'flex', flexDirection: 'column' }}
      className="rounded-xl border border-[#e0d5c5] bg-white shadow-2xl overflow-hidden"
    >
      {children}
    </div>,
    document.body
  );
};

/* ─── Product Dropdown ──────────────────────────────────── */
const ProductDropdown = ({ products, value, onChange, loading, hasError }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const searchRef  = useRef(null);

  const selected = value ? products.find(p => String(p.id) === String(value)) : null;
  const filtered = query.trim()
    ? products.filter(p => p.name.toLowerCase().includes(query.toLowerCase()) || String(p.price ?? p.sale_price ?? '').includes(query))
    : products;

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const close = () => { setOpen(false); setQuery(''); };

  if (loading) {
    return (
      <div className="w-full px-2.5 py-1.5 rounded-md border border-[#e0d5c5] bg-gray-50 text-xs text-gray-400 flex items-center gap-1.5">
        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="20" />
        </svg>
        Loading…
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={
          'w-full px-2.5 py-1.5 rounded-md border text-xs text-left flex items-center justify-between gap-1 transition ' +
          'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ' +
          (hasError ? 'border-red-400 bg-red-50' : open ? 'border-teal-400 bg-gray-50 ring-2 ring-teal-500' : 'border-[#e0d5c5] bg-gray-50 hover:border-teal-300')
        }
      >
        <span className={`flex-1 min-w-0 truncate ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
          {selected ? truncate(selected.name, 22) : 'Select product…'}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && (
            <span onClick={(e) => { e.stopPropagation(); onChange(null); }} className="w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-500 flex items-center justify-center text-gray-400 transition cursor-pointer">
              <X className="w-2.5 h-2.5" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      <PortalDropdown triggerRef={triggerRef} open={open} onClose={close} minWidth={280}>
        <div className="px-3 py-2 border-b border-[#f0e8db] bg-gray-50 shrink-0">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#e0d5c5]">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input ref={searchRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search products…" className="flex-1 text-sm text-gray-700 bg-transparent outline-none placeholder-gray-400" />
            {query && <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600 transition"><X className="w-3 h-3" /></button>}
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">No products match "{query}"</div>
          ) : filtered.map(p => {
            const isSel = String(p.id) === String(value);
            return (
              <button key={p.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { onChange(p); close(); }}
                className={'w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ' + (isSel ? 'bg-teal-50 border-l-2 border-teal-500' : 'hover:bg-gray-50 border-l-2 border-transparent')}
              >
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className="w-9 h-9 rounded-md object-cover border border-[#e8dfd0] shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-md bg-gray-100 border border-[#e8dfd0] flex items-center justify-center shrink-0">
                    <Package className="w-4 h-4 text-gray-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 font-medium truncate" title={p.name}>{highlightMatch(p.name, query)}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs font-semibold text-teal-600">₹{p.price ?? p.sale_price}</span>
                    {p.sku && <span className="text-xs text-gray-400 truncate">SKU: {p.sku}</span>}
                    {p.variations?.length > 0 && (
                      <span className="text-xs text-teal-600 font-medium">{p.variations.length} variants</span>
                    )}
                  </div>
                </div>
                {isSel && <span className="text-teal-500 shrink-0 text-xs font-bold">✓</span>}
              </button>
            );
          })}
        </div>
        <div className="px-4 py-1.5 border-t border-[#f0e8db] bg-gray-50 shrink-0">
          <p className="text-xs text-gray-400">{filtered.length} of {products.length} products</p>
        </div>
      </PortalDropdown>
    </div>
  );
};

/* ─── Variant Dropdown ──────────────────────────────────── */
const VariantDropdown = ({ variations, value, onChange, hasError }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const selected = value ? variations.find(v => String(v.id) === String(value)) : null;
  const close = () => setOpen(false);

  const variantLabel = (v) => {
    if (v.attributes && typeof v.attributes === 'object' && Object.keys(v.attributes).length) {
      return Object.values(v.attributes).join(' / ');
    }
    if (v.name) return v.name;
    if (v.title) return v.title;
    return v.color || v.size || v.material || `Variant #${v.id}`;
  };

  return (
    <div className="relative w-full mt-1.5">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={
          'w-full px-2.5 py-1.5 rounded-md border text-xs text-left flex items-center justify-between gap-1 transition ' +
          'focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent ' +
          (hasError ? 'border-red-400 bg-red-50' : open ? 'border-teal-400 bg-gray-50 ring-2 ring-teal-200' : 'border-gray-200 bg-gray-50 hover:border-gray-300')
        }
      >
        <span className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-teal-700 leading-none">VAR</span>
          <span className={`truncate ${selected ? 'text-gray-800' : 'text-gray-400'}`}>
            {selected ? truncate(variantLabel(selected), 20) : 'Select variant…'}
          </span>
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && (
            <span
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              className="w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-500 flex items-center justify-center text-gray-400 transition cursor-pointer"
            >
              <X className="w-2.5 h-2.5" />
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      <PortalDropdown triggerRef={triggerRef} open={open} onClose={close} minWidth={220}>
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 shrink-0">
          <p className="text-xs font-semibold text-teal-700">Choose a variant</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {variations.map(v => {
            const isSel = String(v.id) === String(value);
            const label = variantLabel(v);
            const price = v.price ?? v.sale_price;
            return (
              <button
                key={v.id}
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(v); close(); }}
                className={
                  'w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 transition-colors ' +
                  (isSel ? 'bg-gray-50 border-l-2 border-teal-400' : 'hover:bg-gray-50 border-l-2 border-transparent')
                }
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{label}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {price != null && <span className="text-xs font-semibold text-teal-600">₹{price}</span>}
                    {v.sku && <span className="text-xs text-gray-400">SKU: {v.sku}</span>}
                  </div>
                </div>
                {isSel && <span className="text-teal-600 shrink-0 text-xs font-bold">✓</span>}
              </button>
            );
          })}
        </div>
      </PortalDropdown>
    </div>
  );
};

/* ─── Work Items Table ──────────────────────────────────── */
const newRow = () => ({ id: crypto.randomUUID(), productId: '', variationId: '', itemDescription: '', quantity: '', unitPrice: '' });

// Grid: Product(2fr) | Description(2fr) | Qty(60px) | UnitPrice+Total(110px) | Delete(22px)
const TABLE_COLS = '2fr 2fr 60px 110px 22px';

const WorkItemsTable = ({ rows, onChange, products, productsLoading, errors }) => {
  const addRow    = () => onChange([...rows, newRow()]);
  const removeRow = (id) => onChange(rows.filter(r => r.id !== id));
  const updateRow = (id, field, value) =>
    onChange(rows.map(r => r.id === id ? { ...r, [field]: value } : r));

  const handleProductSelect = (id, prod) => {
    if (!prod) {
      onChange(rows.map(r => r.id === id ? { ...r, productId: '', variationId: '', itemDescription: '', unitPrice: '' } : r));
      return;
    }
    const hasVariants = prod.variations?.length > 0;
    const rawPrice = prod.price ?? prod.sale_price ?? prod.regular_price ?? prod.base_price;
    const parsedPrice = parseFloat(rawPrice);
    onChange(rows.map(r =>
      r.id === id ? {
        ...r,
        productId:       String(prod.id),
        variationId:     String(prod.id) === String(r.productId) ? r.variationId : '',
        itemDescription: prod.name,
        unitPrice:       hasVariants ? '' : (isNaN(parsedPrice) ? '' : String(parsedPrice)),
        quantity:        r.quantity || '1',
      } : r
    ));
  };

  const handleVariantSelect = (id, variant) => {
    if (!variant) {
      const row  = rows.find(r => r.id === id);
      const prod = row ? products.find(p => String(p.id) === String(row.productId)) : null;
      const rawPrice = prod?.price ?? prod?.sale_price ?? prod?.regular_price;
      const parsedPrice = parseFloat(rawPrice);
      onChange(rows.map(r =>
        r.id === id ? {
          ...r,
          variationId: '',
          unitPrice: isNaN(parsedPrice) ? r.unitPrice : String(parsedPrice),
        } : r
      ));
      return;
    }
    const rawPrice = variant.price ?? variant.sale_price ?? variant.regular_price;
    const parsedPrice = parseFloat(rawPrice);
    onChange(rows.map(r =>
      r.id === id ? {
        ...r,
        variationId: String(variant.id),
        ...(!isNaN(parsedPrice) && { unitPrice: String(parsedPrice) }),
      } : r
    ));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-gray-800">Product Items</p>
          <p className="text-xs text-gray-400">Add one or more items</p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-400
                     text-teal-700 text-xs font-semibold bg-white hover:bg-teal-50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Row
        </button>
      </div>

      <div className="rounded-xl border border-[#e0d5c5] overflow-hidden">
        {/* Header */}
        <div
          className="grid text-xs font-semibold text-white px-3 py-2.5 gap-2"
          style={{ gridTemplateColumns: TABLE_COLS, background: 'linear-gradient(135deg, #0F766E, #14B8A6)' }}
        >
          <span>Select Product / Variant *</span>
          <span>Item Description *</span>
          <span className="text-center">Qty *</span>
          <span className="text-center">Unit Price *</span>
          <span />
        </div>

        {/* Rows */}
        <div className="divide-y divide-[#f0e8db] bg-white">
          {rows.map((row, idx) => {
            const qty   = parseInt(row.quantity, 10)  || 0;
            const price = parseFloat(row.unitPrice)   || 0;
            const total = (qty * price).toFixed(2);
            const e     = errors?.[idx] || {};

            const selectedProduct = row.productId
              ? products.find(p => String(p.id) === String(row.productId))
              : null;
            const hasVariants = selectedProduct?.variations?.length > 0;

            return (
              <div
                key={row.id}
                className="grid items-start gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                style={{ gridTemplateColumns: TABLE_COLS }}
              >
                {/* ── Col 1: Product + Variant ── */}
                <div>
                  <ProductDropdown
                    products={products}
                    value={row.productId}
                    onChange={(prod) => handleProductSelect(row.id, prod)}
                    loading={productsLoading}
                    hasError={!!e.productId}
                  />
                  {e.productId && <p className="text-xs text-red-500 mt-0.5">{e.productId}</p>}
                  {hasVariants && (
                    <VariantDropdown
                      variations={selectedProduct.variations}
                      value={row.variationId}
                      onChange={(variant) => handleVariantSelect(row.id, variant)}
                      hasError={!!e.variationId}
                    />
                  )}
                  {e.variationId && <p className="text-xs text-red-500 mt-0.5">{e.variationId}</p>}
                </div>

                {/* ── Col 2: Item Description ── */}
                <div>
                  <input
                    type="text"
                    placeholder="Auto-filled or type…"
                    value={row.itemDescription}
                    onChange={ev => updateRow(row.id, 'itemDescription', ev.target.value)}
                    maxLength={100}
                    className={
                      'w-full px-2.5 py-1.5 rounded-md border text-xs text-gray-800 placeholder-gray-400 ' +
                      'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition ' +
                      (e.itemDescription ? 'border-red-400 bg-red-50' : 'border-[#e0d5c5] bg-gray-50')
                    }
                  />
                  {e.itemDescription && <p className="text-xs text-red-500 mt-0.5">{e.itemDescription}</p>}
                </div>

                {/* ── Col 3: Quantity ── */}
                <div>
                  <input
                   type="text"
inputMode="numeric" min={1}
                    placeholder="1"
                    value={row.quantity}
                    onChange={ev => updateRow(row.id, 'quantity', ev.target.value)}
                    className={
                      'w-full px-2 py-1.5 rounded-md border text-xs text-gray-800 text-center placeholder-gray-400 ' +
                      'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition ' +
                      (e.quantity ? 'border-red-400 bg-red-50' : 'border-[#e0d5c5] bg-gray-50')
                    }
                  />
                  {e.quantity && <p className="text-xs text-red-500 mt-0.5">{e.quantity}</p>}
                </div>

                {/* ── Col 4: Unit Price + Row Total below ── */}
                <div>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 select-none pointer-events-none">₹</span>
                    <input
                      type="text"
inputMode="numeric" min={0} step={0.01}
                      placeholder="0.00"
                      value={row.unitPrice === '' ? '' : Number(row.unitPrice)}
                      onChange={ev => updateRow(row.id, 'unitPrice', ev.target.value)}
                      className={
                        'w-full pl-5 pr-2 py-1.5 rounded-md border text-xs text-gray-800 text-right placeholder-gray-400 ' +
                        'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition ' +
                        (e.unitPrice ? 'border-red-400 bg-red-50' : 'border-[#e0d5c5] bg-gray-50')
                      }
                    />
                  </div>
                  {/* Row total shown below the price input */}
                  <p className="text-right text-xs font-semibold text-teal-600 mt-1 pr-0.5">
                    = ₹{total}
                  </p>
                  {e.unitPrice && <p className="text-xs text-red-500 mt-0.5">{e.unitPrice}</p>}
                </div>

                {/* ── Col 5: Remove row ── */}
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  disabled={rows.length === 1}
                  className="mt-1.5 w-5 h-5 flex items-center justify-center rounded-full
                             text-gray-300 hover:text-red-400 hover:bg-red-50
                             transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─── Payment Methods ───────────────────────────────────── */
const PAYMENT_METHODS = [
  { value: 'Cod',              label: 'Cash on Delivery'  },
  { value: 'UPI - Google Pay', label: 'UPI - Google Pay'  },
  { value: 'UPI - PhonePe',    label: 'UPI - PhonePe'     },
  { value: 'Net Banking',      label: 'Net Banking'       },
];

/* ─── Initial form state ────────────────────────────────── */
const INITIAL = {
  // customerId:       '',
  customerName:     '',
  customerPhone:    '',
  description:      '',
  paymentMethod:    '',
  paymentStatus:    '',
  orderType:        '',
  shippingCharges:  '',
  tax:              '',
  promoCode:        '',
  promoDiscount:    '',
  transactionId:    '',
  paymentGateway:   '',
  notes:            '',
  billingFirstName: '',
  billingLastName:  '',
  billingEmail:     '',
  billingPhone:     '',
  billingAddress:   '',
  billingCity:      '',
  billingState:     '',
  billingPincode:   '',
};

/* ─── Main Component ────────────────────────────────────── */
const AddOrderModal = ({ open, onClose }) => {
  const { createBulkOrder, loading: orderLoading, error } = useOrders();
  const { products, loading: productsLoading } = useProducts();
  const { paymentTypes, paymentTypesLoading }  = usePaymentTypes();
  const { getSettings } = useSettings();

  const [form,        setForm]        = useState(INITIAL);
  const [rows,        setRows]        = useState([newRow()]);
  const [errors,      setErrors]      = useState({});
  const [rowErrors,   setRowErrors]   = useState([]);
  const [show,        setShow]        = useState(false);
  const [apiError,    setApiError]    = useState('');
  const [success,     setSuccess]     = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [taxRate,     setTaxRate]     = useState(0);

  useEffect(() => {
    getSettings().then(res => {
      const taxVal =
        res?.data?.tax ??
        res?.data?.[0]?.tax ??
        res?.settings?.tax ??
        res?.tax ??
        null;
      if (taxVal !== null) setTaxRate(parseFloat(taxVal) || 0);
    });
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
      setForm(INITIAL);
      setRows([newRow()]);
      setErrors({});
      setRowErrors([]);
      setApiError('');
      setSuccess(false);
      setShowBilling(false);
    } else {
      setShow(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const subtotal        = rows.reduce((acc, r) => acc + (parseInt(r.quantity, 10) || 0) * (parseFloat(r.unitPrice) || 0), 0);
  const tax             = parseFloat(((subtotal * taxRate) / 100).toFixed(2));
  const shippingCharges = parseFloat(form.shippingCharges) || 0;
  const promoDiscount   = parseFloat(form.promoDiscount)   || 0;
  const total           = (subtotal + tax + shippingCharges - promoDiscount).toFixed(2);

  const validate = () => {
    const e = {};
    // if (!form.customerId.trim() || isNaN(parseInt(form.customerId, 10))) {
    //   e.customerId = 'Customer ID is required (integer)';
    // }
    if (!form.customerPhone.trim()) {
      e.customerPhone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(form.customerPhone.trim())) {
      e.customerPhone = 'Enter a valid 10-digit Indian mobile number';
    }
    if (!form.paymentMethod) e.paymentMethod = 'Required';
    if (!form.paymentStatus) e.paymentStatus = 'Required';
if (!form.customerName.trim()) {
  e.customerName = 'Customer name is required';
}
    const re = rows.map(r => {
      const rowE = {};
      if (!r.productId)              rowE.productId       = 'Required';
      if (!r.itemDescription.trim()) rowE.itemDescription = 'Required';
      const q = parseInt(r.quantity, 10);
      if (!r.quantity || isNaN(q) || q < 1)  rowE.quantity  = 'Min 1';
      const p = parseFloat(r.unitPrice);
      if (!r.unitPrice || isNaN(p) || p < 0) rowE.unitPrice = 'Required';
      const prod = products.find(p => String(p.id) === String(r.productId));
      if (prod?.variations?.length > 0 && !r.variationId) {
        rowE.variationId = 'Select a variant';
      }
      return rowE;
    });

    setErrors(e);
    setRowErrors(re);
    return !Object.keys(e).length && re.every(r => !Object.keys(r).length);
  };

 const buildPayload = () => {
  const products = rows.map(r => {
    const product = {
      product_id: parseInt(r.productId, 10),
      quantity:   parseInt(r.quantity, 10),
      price:      parseFloat(r.unitPrice),
    };
    const variationId = parseInt(r.variationId, 10);
    if (!isNaN(variationId)) {
      product.variation_id = variationId;
    }
    if (r.itemDescription.trim()) {
      product.item_description = r.itemDescription.trim();
    }
    return product;
  });

  const order = {
    shipping_charges: parseFloat(form.shippingCharges) || 0,
    tax,
    ...(form.promoCode.trim() && { promo_code:     form.promoCode.trim() }),
    ...(form.promoDiscount    && { promo_discount: parseFloat(form.promoDiscount) || 0 }),
    payment_method: form.paymentMethod,
    payment_status: form.paymentStatus,
    ...(form.transactionId.trim()  && { transaction_id:   form.transactionId.trim() }),
    ...(form.paymentGateway.trim() && { payment_gateway:  form.paymentGateway.trim() }),
    ...(form.billingFirstName.trim() && { billing_first_name: form.billingFirstName.trim() }),
    ...(form.billingLastName.trim()  && { billing_last_name:  form.billingLastName.trim() }),
    ...(form.billingEmail.trim()     && { billing_email:      form.billingEmail.trim() }),
    ...(form.billingPhone.trim()     && { billing_phone:      form.billingPhone.trim() }),
    ...(form.billingAddress.trim()   && { billing_address:    form.billingAddress.trim() }),
    ...(form.billingCity.trim()      && { billing_city:       form.billingCity.trim() }),
    ...(form.billingState.trim()     && { billing_state:      form.billingState.trim() }),
    ...(form.billingPincode.trim()   && { billing_pincode:    form.billingPincode.trim() }),
    products,
  };

 return {
  user_name:   form.customerName.trim(),
  user_phone:  form.customerPhone.trim(),
  description: (form.description.trim() || form.notes.trim()) || '',
  ...(form.orderType && { order_type: parseInt(form.orderType, 10) }),
  orders: [order],
};
};

  const handleSubmit = async () => {
    
    setApiError('');
    if (!validate()) return;
    const payload = buildPayload();
    console.log('Sending bulk-order payload:', JSON.stringify(payload, null, 2));
    const result = await createBulkOrder(payload);
    if (result) {
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1500);
    } else {
      setApiError(error || 'Failed to create order. Please try again.');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300" onClick={onClose} aria-hidden="true" />

      <div
        className="fixed inset-0 z-50 flex"
        style={{ backgroundColor: show ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)', transition: 'background-color 300ms' }}
        onClick={onClose}
      >
        <div
          className="ml-auto h-full flex flex-col shadow-2xl relative"style={{ width: 'min(680px, 100vw)', backgroundColor: '#ffffff', transform: show ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 300ms cubic-bezier(.4,0,.2,1)' }} style={{ width: 'min(680px, 100vw)', backgroundColor: '#fdf8f2', transform: show ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 300ms cubic-bezier(.4,0,.2,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 transition-colors z-10" aria-label="Close">
            <X className="w-4 h-4" />
          </button>

          {/* Header */}
        <div
  className="shrink-0 flex flex-col items-start pt-7 pb-5 border-b bg-teal-800 px-6"
  style={{ borderColor: '#e8dfd0' }}
>
  <h2 className="w-full text-2xl font-bold text-white text-left tracking-tight">
    Add New Order
  </h2>
</div>
          {apiError && (
            <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">{apiError}</div>
          )}
          {success && (
            <div className="mx-4 mt-3 px-4 py-2 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-700 font-medium text-center">✅ Order created successfully!</div>
          )}

         {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto pt-2 pb-2 bg-gray-50">
            <div className="p-8 space-y-8 min-h-[700px]">
              {/* Customer Details */}
              <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">Customer Details</p>

            
               <Field label="Customer Name" required error={errors.customerName} helper="Display name for the order">
                  <input type="text" className={inp} placeholder="Enter customer name" value={form.customerName} onChange={set('customerName')} />
                </Field>
                
              

              <Field label="Phone Number" required helper="10-digit Indian mobile number" error={errors.customerPhone}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium select-none pointer-events-none">+91</span>
                  <input type="tel" className={inp + ' pl-12'} placeholder="98765 43210" value={form.customerPhone} onChange={set('customerPhone')} maxLength={10} />
                </div>
              </Field>

              <Field label="Description" helper="Order description / remarks">
                <input type="text" className={inp} placeholder="e.g. Bulk order for event" value={form.description} onChange={set('description')} />
              </Field>

              {/* Order Details */}
              <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">Order Details</p>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Payment Method" required helper="How the customer will pay" error={errors.paymentMethod}>
                  <div className="relative">
                    <select className={inp + ' appearance-none cursor-pointer pr-8'} value={form.paymentMethod} onChange={set('paymentMethod')}>
                      <option value="">Select method…</option>
                      {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                  </div>
                </Field>

                <Field label="Order Type" helper="Optional">
                  <div className="relative">
                    {paymentTypesLoading ? (
                      <div className={inp + ' text-gray-400'}>Loading order types…</div>
                    ) : (
                      <>
                       <select className={inp + ' appearance-none cursor-pointer pr-8'} value={form.orderType} onChange={set('orderType')}>
  <option value="">Select order type…</option>
  {paymentTypes.filter(t => t.status === 'Active').map(t => 
    <option key={t.id} value={t.id}>{t.type}</option>
  )}
</select>
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                      </>
                    )}
                  </div>
                </Field>
              </div>

              <Field label="Payment Status" required error={errors.paymentStatus}>
                <div className="flex gap-3 flex-wrap">
                  {['Pending', 'Paid'].map(s => (
                    <label key={s} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer text-sm transition-all ${form.paymentStatus === s ? 'bg-teal-50 border-teal-400 text-teal-700 font-medium' : 'border-[#e0d5c5] bg-gray-50 text-gray-600 hover:border-teal-300'}`}>
                      <input type="radio" name="paymentStatus" value={s} checked={form.paymentStatus === s} onChange={set('paymentStatus')} className="accent-teal-600" />
                      {s}
                    </label>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Transaction ID" helper="Optional">
                  <input type="text" className={inp} placeholder="TXN12345" value={form.transactionId} onChange={set('transactionId')} />
                </Field>
                <Field label="Payment Gateway" helper="Optional">
                  <input type="text" className={inp} placeholder="Razorpay" value={form.paymentGateway} onChange={set('paymentGateway')} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Shipping Charges" helper="Optional">
                  <input type="text"
inputMode="numeric" min={0} step={0.01} className={inp} placeholder="0.00" value={form.shippingCharges} onChange={set('shippingCharges')} />
                </Field>
                <Field label="Promo Discount" helper="Optional">
                  <input type="text"
inputMode="numeric" min={0} step={0.01} className={inp} placeholder="0.00" value={form.promoDiscount} onChange={set('promoDiscount')} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Promo Code" helper="Optional">
                  <input type="text" className={inp} placeholder="SAVE10" value={form.promoCode} onChange={set('promoCode')} />
                </Field>
              </div>

              {/* Billing Details */}
              <div>
                <button type="button" onClick={() => setShowBilling(s => !s)} className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-4 flex items-center gap-2">
                  Billing Details (optional)
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showBilling ? 'rotate-180' : ''}`} />
                </button>
                {showBilling && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Billing First Name"><input type="text" className={inp} value={form.billingFirstName} onChange={set('billingFirstName')} /></Field>
                      <Field label="Billing Last Name"><input type="text" className={inp} value={form.billingLastName} onChange={set('billingLastName')} /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Billing Email"><input type="email" className={inp} value={form.billingEmail} onChange={set('billingEmail')} /></Field>
                      <Field label="Billing Phone"><input type="tel" className={inp} value={form.billingPhone} onChange={set('billingPhone')} /></Field>
                    </div>
                    <Field label="Billing Address"><input type="text" className={inp} value={form.billingAddress} onChange={set('billingAddress')} /></Field>
                    <div className="grid grid-cols-3 gap-4">
                      <Field label="City"><input type="text" className={inp} value={form.billingCity} onChange={set('billingCity')} /></Field>
                      <Field label="State"><input type="text" className={inp} value={form.billingState} onChange={set('billingState')} /></Field>
                      <Field label="Pincode"><input type="text" className={inp} value={form.billingPincode} onChange={set('billingPincode')} /></Field>
                    </div>
                  </div>
                )}
              </div>

              {/* Item Details */}
              <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-4 mt-1">Item Details</p>

              <WorkItemsTable
                rows={rows}
                onChange={setRows}
                products={products}
                productsLoading={productsLoading}
                errors={rowErrors}
              />

              {/* Order Summary */}
              {subtotal > 0 && (
                <div className="rounded-xl bg-teal-50 border border-teal-200 overflow-hidden">
                  <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                    <span className="text-sm text-teal-600">Subtotal</span>
                    <span className="text-sm font-medium text-teal-700">₹{subtotal.toFixed(2)}</span>
                  </div>
                  {shippingCharges > 0 && (
                    <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                      <span className="text-sm text-teal-600">Shipping Charges</span>
                      <span className="text-sm font-medium text-teal-700">₹{shippingCharges.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                    <span className="text-sm text-teal-600">GST ({taxRate}%)</span>
                    <span className="text-sm font-medium text-teal-700">₹{tax.toFixed(2)}</span>
                  </div>
                  {promoDiscount > 0 && (
                    <div className="px-4 py-2 flex items-center justify-between border-b border-teal-100">
                      <span className="text-sm text-teal-600">Promo Discount</span>
                      <span className="text-sm font-medium text-teal-700">-₹{promoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="px-4 py-3 flex items-center justify-between bg-teal-100">
                    <span className="text-sm text-teal-700 font-semibold">Total</span>
                    <span className="text-lg font-bold text-teal-800">₹{total}</span>
                  </div>
                </div>
              )}

              <Field label="Notes" helper="Special instructions or remarks" counter maxLen={200} valueLen={form.notes.length}>
                <textarea rows={3} className={inp + ' resize-none'} placeholder="e.g. Please deliver before 5 PM…" maxLength={200} value={form.notes} onChange={set('notes')} />
              </Field>

              {/* Actions */}
              <div className="flex w-full gap-3 pt-2">
                <button onClick={onClose} disabled={orderLoading} className="flex-1 px-5 py-2.5 rounded-lg border border-[#d0c0a8] text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={handleSubmit} disabled={orderLoading || success} className="flex-1 px-6 py-2.5 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center gap-2">
                  {orderLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="20" />
                      </svg>
                      Creating…
                    </>
                  ) : 'Create Order'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddOrderModal;